import FormSectionSubtitle from "@rentiohq/shared-frontend/dist/components/components/FormSectionSubtitle";
import ListItem from "@rentiohq/shared-frontend/dist/components/components/ListItem";
import ListItemSeparator from "@rentiohq/shared-frontend/dist/components/components/ListItemSeparator";
import Spacer, {
  ESpacerDirection,
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import * as paymentOrderUtils from "@rentiohq/shared-frontend/dist/utils/paymentOrder.utils";
import { join } from "@rentiohq/shared-frontend/dist/utils/string.utils";
import {
  EPaymentMethod,
  EPaymentRepetitionType,
  IPaymentRequest,
} from "@rentiohq/shared/dist/types/payment.types";
import { IProperty } from "@rentiohq/shared/dist/types/property.types";
import { max } from "@rentiohq/shared/dist/utils/date-fns.utils";
import { formatCurrency } from "@rentiohq/shared/dist/utils/number.utils";
import {
  getSepaDueDate,
  getSepaLatestApprovalDate,
} from "@rentiohq/shared/dist/utils/payment.utils";
import React from "react";
import images from "../../../../assets";
import * as SPaymentMethods from "../../routes/PaymentMethodSelect/PaymentMethodSelect.styles";
import * as S from "./Receipt.styles";

const NOW = new Date();

interface IProps {
  paymentRequest: IPaymentRequest;
  property?: IProperty;
  sepaBankDaysOffset?: number;
}

class Receipt extends React.Component<IProps> {
  // Render
  public render() {
    const { paymentRequest, property, sepaBankDaysOffset } = this.props;
    const { paymentRequestItems, createdAt } = paymentRequest;

    const isRecurring =
      paymentRequest.repetitionType !== EPaymentRepetitionType.Once;

    const sepaApprovalDate = formatDate(
      getSepaLatestApprovalDate(paymentRequest, sepaBankDaysOffset),
    );
    const sepaDueDate = formatDate(
      getSepaDueDate(paymentRequest, sepaBankDaysOffset),
    );
    const dueDate = formatDate(max([NOW, paymentRequest.dueDateAt]));
    const paymentMethods = join(
      paymentRequest.allowedPaymentMethods
        ?.map(x => getLocalizedText(`payment_request.method.${x}`))
        .sort(),
      ", ",
      ` ${getLocalizedText("system.or").toLowerCase()} `,
    );

    return (
      <S.Wrap>
        <S.InnerWrap>
          <S.HeaderWrap>
            <S.HeaderLogo src={images.IMG_RENTIO_LOGO} />
            <S.HeaderTitle>{formatDate(createdAt)}</S.HeaderTitle>
          </S.HeaderWrap>

          <Spacer weight={ESpacerWeight.W32} />

          <FormSectionSubtitle
            title={
              property
                ? formatAddress(property)
                : getLocalizedText("payment.receipt.no_property")
            }
          />

          {paymentRequestItems.map(
            ({ id, paymentOrder, amount, startedAt, endedAt }) => {
              let subtitle: string | undefined = undefined;
              if (isRecurring) {
                subtitle = formatDate(startedAt);
                if (endedAt) {
                  subtitle = `${subtitle} - ${formatDate(endedAt)}`;
                }
              }

              return (
                <React.Fragment key={id}>
                  <ListItem
                    title={paymentOrderUtils.getTitle(paymentOrder)}
                    subtitle={subtitle}
                    disclosureInfo={
                      <S.Value>{formatCurrency(amount, true)}</S.Value>
                    }
                  />
                  <S.ListItemSeparatorWrap>
                    <ListItemSeparator />
                  </S.ListItemSeparatorWrap>
                </React.Fragment>
              );
            },
          )}

          <ListItem
            title={
              <S.TotalTitle>
                {getLocalizedText("payment.receipt.total")}
              </S.TotalTitle>
            }
            disclosureInfo={
              <S.TotalValue>
                {formatCurrency(paymentRequest.originalAmount, true)}
              </S.TotalValue>
            }
          />

          <Spacer weight={ESpacerWeight.W32} />

          <S.PayDateInfoWrap>
            {paymentRequestItems.length > 0 && (
              <>
                <S.PayBeforeDate>
                  {getLocalizedText(`payment.receipt.pay_before.unified`, {
                    sepaApprovalDate,
                    sepaDueDate,
                    dueDate,
                    paymentMethods,
                  })}
                </S.PayBeforeDate>

                {paymentRequest.allowedPaymentMethods.includes(
                  EPaymentMethod.Sepa,
                ) && (
                  <>
                    <Spacer weight={ESpacerWeight.W04} />
                    <S.PayCompletedDate>
                      {getLocalizedText(
                        `payment.receipt.pay_completed.unified`,
                        {
                          sepaApprovalDate,
                          sepaDueDate,
                          dueDate,
                          paymentMethods,
                        },
                      )}
                    </S.PayCompletedDate>
                  </>
                )}

                {this.renderPaymentMethodIcons(paymentRequest)}
              </>
            )}
          </S.PayDateInfoWrap>
        </S.InnerWrap>
      </S.Wrap>
    );
  }

  private renderPaymentMethodIcons = (paymentRequest: IPaymentRequest) => {
    const allowedPaymentMethods = paymentRequest.allowedPaymentMethods;

    return (
      <S.PaymentMethodIconWrap>
        {allowedPaymentMethods.map((paymentMethod, index) => (
          <>
            {index > 0 && (
              <Spacer
                weight={ESpacerWeight.W04}
                direction={ESpacerDirection.Horizontal}
              />
            )}
            {this.renderPaymentMethodIcon(paymentMethod)}
          </>
        ))}
      </S.PaymentMethodIconWrap>
    );
  };

  private renderPaymentMethodIcon = (paymentMethod: EPaymentMethod) => {
    switch (paymentMethod) {
      case EPaymentMethod.Sepa:
        return <SPaymentMethods.PaymentMethodIconSepa />;

      case EPaymentMethod.Bancontact:
        return <SPaymentMethods.PaymentMethodIconBcmc />;

      case EPaymentMethod.Maestro:
        return <SPaymentMethods.PaymentMethodIconIdeal />;

      // case EPaymentMethod.Ibanisation:
      //   return <SPaymentMethods.PaymentMethodIconIbanisation />;

      case EPaymentMethod.Ideal:
        return <SPaymentMethods.PaymentMethodIconMaestro />;

      default:
        break;
    }
  };
}

export default Receipt;
